import React, { useState } from 'react';
import { Pencil, Trash, Plus } from 'lucide-react';
import { useSyncedStore } from '@syncedstore/react';
import { todoStore, addTodo, editTodo, toggleTodo, deleteTodo } from 'src/store/todo';
import type { Todo } from 'src/store/todo';

const ToDoCard: React.FC<{
  todo: Todo;
  onEdit: (id: string, newTitle: string) => void;
  onDelete: (id: string) => void;
  onToggleDone: (id: string) => void;
}> = ({ todo, onEdit, onDelete, onToggleDone }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(todo.title);

  const handleEdit = () => {
    if (isEditing && editValue.trim()) {
      onEdit(todo.id, editValue);
    }
    setIsEditing(!isEditing);
  };

  return (
    <div className="border rounded-md shadow-md">
      <div className="p-4 flex justify-between items-center">
        <input
          type="checkbox"
          checked={todo.completed}
          onChange={() => onToggleDone(todo.id)}
          className="mr-4"
          disabled={isEditing}
        />
        {isEditing ? (
          <input
            className="border p-2 rounded-md w-full mr-4"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleEdit();
              if (e.key === 'Escape') setIsEditing(false);
            }}
            onBlur={handleEdit}
            autoFocus
          />
        ) : (
          <span className={`flex-1 mr-4 ${todo.completed ? 'line-through text-gray-500 opacity-60' : ''}`}>
            {todo.title}
          </span>
        )}
        <div className="flex gap-2">
          <Pencil
            className="text-blue-500 cursor-pointer w-5 h-5"
            onClick={handleEdit}
          />
          <Trash
            className="text-red-500 cursor-pointer w-5 h-5"
            onClick={() => onDelete(todo.id)}
          />
        </div>
      </div>
      {/* Metadata section */}
      <div className="px-4 pb-3 border-t border-dashed border-gray-300">
        <div className="text-xs text-gray-500 mt-2 flex justify-between">
          <span>{todo.timestamp ? new Date(todo.timestamp).toLocaleString() : '-'}</span>
          <span>{todo.clientId !== null ? `ID: ${todo.clientId}` : '-'}</span>
        </div>
      </div>
    </div>
  );
};

const ToDoPage: React.FC = () => {

  const state = useSyncedStore(todoStore);
  const [newToDo, setNewToDo] = useState('');

  const handleAddToDo = () => {
    if (newToDo.trim()) {
      try {
        addTodo(newToDo.trim());
        setNewToDo('');
      } catch (error) {
        console.error('Error adding todo:', error);
      }
    }
  };

  const handleEditToDo = (id: string, newText: string) => {
    try {
      editTodo(id, newText);
    } catch (error) {
      console.error('Error editing todo:', error);
    }
  };

  const handleDeleteToDo = (id: string) => {
    try {
      deleteTodo(id);
    } catch (error) {
      console.error('Error deleting todo:', error);
    }
  };

  const handleToggleDone = (id: string) => {
    try {
      toggleTodo(id);
    } catch (error) {
      console.error('Error toggling todo:', error);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleAddToDo();
    }
  };

  console.log({todos: state.todos})
  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-4">To-Do List</h1>
      <div className="flex mb-4">
        <input
          className="border p-2 rounded-md flex-1 mr-4"
          placeholder="Add a new task"
          value={newToDo}
          onChange={(e) => setNewToDo(e.target.value)}
          onKeyPress={handleKeyPress}
        />
        <Plus
          className="bg-green-500 text-white p-2 rounded-md cursor-pointer"
          onClick={handleAddToDo}
        />
      </div>
      <div className="space-y-4">
        {state.todos.map((todo) => (
          <ToDoCard
            key={todo.id}
            todo={todo}
            onEdit={handleEditToDo}
            onDelete={handleDeleteToDo}
            onToggleDone={handleToggleDone}
          />
        ))}
      </div>
      {/* Status indicator */}
      <div className="mt-6 text-center text-sm text-gray-500">
        Offline No Problem, LOL
      </div>
    </div>
  );
};

export default ToDoPage;
